import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

/**
 * M19 L19.08 — Platform status page.
 *
 * Reads `GET /v1/platform/health/status` and renders the current health of
 * every monitored component.
 */
interface HealthRow {
  readonly healthCheckId: string;
  readonly component: string;
  readonly status: string;
  readonly healthScore: number | null;
  readonly responseTimeMs: number | null;
  readonly checkedAt: string;
}

function statusColor(status: string): string {
  const s = status.toUpperCase();
  if (s === 'HEALTHY' || s === 'UP' || s === 'OK') return 'var(--color-success)';
  if (s === 'DEGRADED' || s === 'WARN') return 'var(--color-warning)';
  return 'var(--color-danger)';
}

export function PlatformStatusPage(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [rows, setRows] = useState<HealthRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    apiRequest<{ data: HealthRow[] }>('/v1/platform/health/status', { token })
      .then((r) => { if (alive) setRows(r.data ?? []); })
      .catch((e) => { if (alive) { setError((e as Error).message); setRows([]); } });
    return () => { alive = false; };
  }, [token]);

  return (
    <main aria-label="platform-status">
      <h1 style={{ fontSize: 20, marginBottom: 4 }}>{t('m19.platformStatus.title')}</h1>
      <p style={{ color: 'var(--color-text-muted)', marginBottom: 16, fontSize: 13 }}>{t('m19.platformStatus.subtitle')}</p>

      {error && <p role="alert" style={{ color: 'var(--color-danger)' }}>{error}</p>}
      {!rows ? (
        <p>{t('m19.platformStatus.loading')}</p>
      ) : rows.length === 0 ? (
        <p data-testid="status-empty" style={{ color: 'var(--color-text-muted)' }}>{t('m19.platformStatus.empty')}</p>
      ) : (
        <ul aria-label="status-list" style={{ listStyle: 'none', padding: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {rows.map((h) => (
            <li key={h.healthCheckId} data-testid={`status-row-${h.healthCheckId}`}
                style={{ display: 'flex', alignItems: 'center', gap: 12, border: '1px solid var(--color-border-default)', borderRadius: 6, padding: 12 }}>
              <span aria-hidden style={{ width: 10, height: 10, borderRadius: '50%', background: statusColor(h.status) }} />
              <strong style={{ flex: 1 }}>{h.component}</strong>
              <span data-testid={`status-value-${h.healthCheckId}`} style={{ color: statusColor(h.status), fontWeight: 600 }}>{h.status}</span>
              {h.responseTimeMs != null && <span style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>{t('m19.platformStatus.responseTime', { ms: h.responseTimeMs })}</span>}
              {h.healthScore != null && <span style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>{t('m19.platformStatus.score', { score: h.healthScore })}</span>}
            </li>
          ))}
        </ul>
      )}
    </main>
  );
}
